import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  TemplateRef,
  viewChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { EapAddComponent } from '@sondermind/features/account-edit';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { RadioOption } from '@sondermind/iris/radio-group';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';

@Component({
  selector: 'client-setup-eap-info',
  templateUrl: './setup-eap-info.component.html',
  styleUrls: ['./setup-eap-info.component.scss'],
  standalone: false
})
export class SetupEAPInfoComponent implements OnInit {
  @Input() useInSessionRequest: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() user!: ClientUser;
  @Input() fromDirectBooking: boolean = false;
  // emit true if BV error, false if not
  @Output() eapInfoSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() eapSkippedToInsurance: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(EapAddComponent) eapFormComponent;

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  addingEapControl = new UntypedFormControl(false);
  submitting: boolean = false;
  yesOrNo: Array<RadioOption<boolean>> = [
    { name: 'EAP (Employee assistance program)', value: true },
    { name: 'Self-pay, without EAP', value: false }
  ];
  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  ngOnInit(): void {
    this.setEap();
  }

  /**
   * Check if the new user can click the next button based on what forms are available
   * and their status. If a form is active but not able to submit, do not allow next.
   */
  get allowNext(): boolean {
    if (Boolean(this.addingEapControl.value) && !this.canSubmitEap) { return false }

    return true;
  }

  get canSubmitEap(): boolean {
    return this.eapFormComponent?.eapInfoForm && !this.eapFormComponent.eapInfoForm.invalid;
  }

  /**
   * Submit EAP or self pay depending on form choice.
   * If user had selected both EAP and Insurance in match flow but declines to enter EAP,
   * send them to insurance without marking as self pay.
   */
  newClientSubmit(): void {
    if (this.addingEapControl.value) {
      this.submitEap();
    } else if (!this.addingEapControl.value && this.user.hasCashPref) {
      this.submitSelfPayOnly(false);
    } else {
      this.eapSkippedToInsurance.emit();
    }
  }

  /**
   * Submit EAP form.
   * If user selected self pay in MF, submit that as well unless BV fails.
   * If BV fails and EAP is resubmitted, and then submit self pay if needed.
   */
  submitEap(): void {
    this.startSubmitting();

    if (this.eapFormComponent.eapInfoForm.invalid) {
      this.stopSubmitting();
      return;
    }

    this.eapFormComponent.save().pipe(
      switchMap(() => {
        if (this.user.hasCashPref) {
          return this.eapFormComponent.saveSelfPay$();
        }
        return of(null);
      }),
      finalize(() => {
        this.stopSubmitting();
      })
    ).subscribe({
      next: () => {
        this.eapInfoSubmitted.emit(false);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);

        // If BV fails once, form will show message prompting user to check info and resubmit
        // if BV fails a second time, show insurance review screen and allow user to proceed
        if (err?.bvFailed === 1) {
          this.eapFormComponent.handleBVFailure(err?.bvFailed);
        } else if (err?.bvFailed > 1) {
          if (this.user.hasCashPref) {
            this.submitSelfPayOnly(true);
          } else {
            this.eapInfoSubmitted.emit(true);
          }
        }
      }
    });
  }

  /**
  * If client does not wish to use insurance with EAP or did not fill out EAP form, submit SelfPay
  */
  submitSelfPayOnly(requiresReview: boolean): void {
    this.startSubmitting();

    this.eapFormComponent.saveSelfPay$().pipe(
      finalize(() => {
        this.stopSubmitting();
      })
    ).subscribe({
      next: () => {
        this.eapInfoSubmitted.emit(requiresReview);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  /**
   * Wrapper for better unit tests.
   */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
   * Wrapper for better unit tests.
   */
  stopSubmitting(): void {
    this.submitting = false;
  }

  /**
   * Default EAP selection based on user preference.
   */
  setEap(): void {
    this.addingEapControl.setValue(!!this.user?.hasEapPref);
  }
}
